import { ElementControllerHost } from "../shared/core/controllers/ElementControllerHost";
import { FrameController } from "../shared/core/controllers/FrameController";
import { prefersReducedMotion } from "../shared/Accessibility";
import type { HomepageAudio } from "./Audio";
import { requiredChild, requiredElement } from "./Dom";
import type { LogoKey } from "./Types";
import { keyPoseTransform } from "./KeyPose";
import { KeyMotion } from "./KeyMotion";

type InputKind = "keyboard" | "pointer" | "touch";
export type InputId = number | string;

export interface KeyControls {
	move(clientX: number, clientY: number): void;
	release(playReleaseSound: boolean): void;
}

interface ActiveInput {
	controls: KeyControls;
	id: InputId;
	kind: InputKind;
}

interface KeyInputs {
	activePointerKeys: Map<InputId, KeyControls>;
	activeTouchKeys: Map<InputId, KeyControls>;
	nativeTouchPointers: Map<number, Pick<KeyControls, "release">>;
	hasNativeTouchEvents: boolean;
}

export function registerKey(
	key: LogoKey,
	audio: HomepageAudio,
	onKey: (key: LogoKey) => void,
	{
		activePointerKeys,
		activeTouchKeys,
		nativeTouchPointers,
		hasNativeTouchEvents,
	}: KeyInputs,
): void {
	const elem = requiredElement<SVGGElement>(key);
	const host = new ElementControllerHost(elem);
	const frames = new FrameController(host);
	const cap = requiredChild<SVGGElement>(elem, ".c");
	const capBox = cap.getBBox();
	const lowerShell = elem.children[0];
	if (!lowerShell) {
		throw new Error(`Missing lower shell for ${key}`);
	}
	const capParentNode = cap.parentNode;
	if (!(capParentNode instanceof SVGGElement)) {
		throw new TypeError(`Missing cap group for ${key}`);
	}
	const capParent = capParentNode;
	const capTransform = capParent.transform.baseVal.consolidate();
	if (!capTransform) {
		throw new Error(`Missing cap transform for ${key}`);
	}
	const capOrigin = capTransform.matrix;
	const lowerOrigin = document.createElementNS(
		"http://www.w3.org/2000/svg",
		"g",
	);
	const lower = document.createElementNS("http://www.w3.org/2000/svg", "g");
	const lowerContent = document.createElementNS(
		"http://www.w3.org/2000/svg",
		"g",
	);
	lowerOrigin.setAttribute(
		"transform",
		`translate(${capOrigin.e} ${capOrigin.f})`,
	);
	lower.classList.add("b");
	lowerContent.setAttribute(
		"transform",
		`translate(${-capOrigin.e} ${-capOrigin.f})`,
	);
	lowerContent.append(lowerShell);
	lower.append(lowerContent);
	lowerOrigin.append(lower);
	elem.prepend(lowerOrigin);
	const width = lower.getBBox().width;
	const motion = new KeyMotion();
	const keyboardId = `keyboard-${key}`;
	let fallbackOnClick = false;
	let pressed = false;
	let activeInput: ActiveInput | undefined;

	function renderPose(): void {
		const pose = prefersReducedMotion()
			? { depth: motion.pose.depth, x: 0, y: 0 }
			: motion.pose;
		cap.style.transform = keyPoseTransform(pose, width, width * 0.64);
		lower.style.transform = keyPoseTransform(pose, width, width * 0.12);
	}

	function stopMotion(): void {
		frames.timeline.pause();
		cap.style.removeProperty("will-change");
		lower.style.removeProperty("will-change");
	}

	function requestMotion(immediate = false): void {
		if (immediate || prefersReducedMotion()) {
			motion.snap();
			stopMotion();
			renderPose();
			return;
		}
		cap.style.willChange = "transform";
		lower.style.willChange = "transform";
		frames.timeline.resume();
	}

	frames.addEventListener("update", (event) => {
		// SAFETY: this handler receives only FrameController's update events.
		const { deltaMs } = (event as CustomEvent<{ deltaMs: number }>).detail;
		if (prefersReducedMotion()) {
			motion.snap();
			stopMotion();
		} else if (!motion.step(deltaMs)) {
			stopMotion();
		}
	});
	frames.addEventListener("draw", renderPose);

	function updatePressPose(clientX: number, clientY: number): void {
		// Measure against the unanimated cap, so a quick re-press cannot move the contact centre.
		const matrix = capParent.getScreenCTM();
		if (!matrix) {
			return;
		}
		const point = new DOMPoint(clientX, clientY).matrixTransform(
			matrix.inverse(),
		);
		let x = ((point.x - capBox.x) / capBox.width) * 2 - 1;
		let y = ((point.y - capBox.y) / capBox.height) * 2 - 1;
		x = Math.max(-1, Math.min(1, x));
		y = Math.max(-1, Math.min(1, y));
		const distance = Math.max(1, Math.hypot(x, y));
		motion.target.x = x / distance;
		motion.target.y = y / distance;
	}

	function move(clientX: number, clientY: number): void {
		if (!pressed) {
			return;
		}
		updatePressPose(clientX, clientY);
		requestMotion();
	}

	function release(playReleaseSound: boolean): void {
		if (!pressed || !activeInput) {
			return;
		}
		pressed = false;

		const inputs =
			activeInput.kind === "touch" ? activeTouchKeys : activePointerKeys;
		if (inputs.get(activeInput.id) === activeInput.controls) {
			inputs.delete(activeInput.id);
		}
		activeInput = undefined;
		for (const [pointerId, input] of nativeTouchPointers) {
			if (input.release === release) {
				nativeTouchPointers.delete(pointerId);
			}
		}

		if (playReleaseSound) {
			if (fallbackOnClick) {
				audio.wakeKeyAudio();
			} else {
				audio.playKeySound("keyup.mp3");
			}
		} else {
			fallbackOnClick = false;
		}
		motion.target.depth = 0;
		motion.target.x = 0;
		motion.target.y = 0;
		requestMotion(!playReleaseSound);
	}

	function press(
		kind: InputKind,
		id: InputId,
		clientX: number,
		clientY: number,
	): void {
		const inputs = kind === "touch" ? activeTouchKeys : activePointerKeys;
		inputs.get(id)?.release(false);
		if (pressed) {
			release(false);
		}

		const controls = { move, release };
		pressed = true;
		activeInput = { kind, id, controls };
		inputs.set(id, controls);
		fallbackOnClick = !audio.playKeySound("keydown.mp3");
		motion.target.depth = 1;
		if (kind === "keyboard") {
			motion.target.x = 0;
			motion.target.y = 0;
		} else {
			updatePressPose(clientX, clientY);
		}
		requestMotion();
	}

	elem.setAttribute("role", "button");
	elem.setAttribute("tabindex", "0");
	elem.setAttribute("aria-label", `${key} key`);

	elem.addEventListener("click", () => {
		// Safari reliably delivers click even when an SVG misses pointerup.
		release(true);
		if (fallbackOnClick && !audio.playKeySound("keydown.mp3")) {
			audio.playFallbackKeySound("keydown.mp3");
		}
		fallbackOnClick = false;
		onKey(key);
	});

	elem.addEventListener("pointerdown", (event) => {
		if (event.pointerType === "touch" && hasNativeTouchEvents) {
			// Native touch events drive movement in Safari, while this
			// independent pointer path guarantees a real end/cancel can
			// release the key without imposing a maximum hold duration.
			nativeTouchPointers.set(event.pointerId, { release });
			return;
		}
		press("pointer", event.pointerId, event.clientX, event.clientY);
		try {
			elem.setPointerCapture?.(event.pointerId);
		} catch {
			// The global listeners still track browsers that cannot capture SVG pointers.
		}
	});

	elem.addEventListener(
		"touchstart",
		(event) => {
			const touch = event.changedTouches[0];
			if (touch) {
				press("touch", touch.identifier, touch.clientX, touch.clientY);
			}
		},
		{ passive: true },
	);

	elem.addEventListener("keydown", (event) => {
		if (
			(event.key !== " " && event.key !== "Enter") ||
			event.repeat ||
			activeInput?.id === keyboardId
		) {
			return;
		}
		event.preventDefault();
		const bounds = cap.getBoundingClientRect();
		press(
			"keyboard",
			keyboardId,
			bounds.left + bounds.width / 2,
			bounds.top + bounds.height / 2,
		);
	});

	elem.addEventListener("keyup", (event) => {
		if (event.key !== " " && event.key !== "Enter") {
			return;
		}
		event.preventDefault();
		if (activeInput?.id !== keyboardId) {
			return;
		}
		release(true);
		onKey(key);
	});

	elem.addEventListener("blur", () => {
		if (activeInput?.id === keyboardId) {
			release(false);
		}
	});

	elem.addEventListener("selectstart", (event) => event.preventDefault());
	elem.addEventListener("contextmenu", (event) => event.preventDefault());
	elem.addEventListener("dragstart", (event) => event.preventDefault());
}
